import { allCountries as allCountyRegions, CountrySlug } from 'country-region-data';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormTextField, FormSearchableSelect } from '../../../../lib/components';
import { ProfileForm, ProfileFormProps } from '../shared/profileForm';
import { countryAlpha2, countryOptions } from '../../../../misc/countries';

type Props = Omit<ProfileFormProps, 'children'>;

export const AddressDataForm: FC<Props> = (props) => {
  const { t, i18n } = useTranslation();

  const language = i18n.resolvedLanguage;
  const countryNames = useMemo(() => countryOptions(language), [language]);

  const [country, setCountry] = useState<CountrySlug | undefined>(
    () => countryAlpha2(props.initialValues?.country) as CountrySlug | undefined
  );

  const provinceNames = useMemo(() => {
    if (!country) return [];
    const match = allCountyRegions.find((c) => c[1] === country);
    if (!match) return [];
    return match[2].map((p) => ({
      text: p[0],
      value: p[0]
    }));
  }, [country]);

  const [missingFields, setMissingFields] = useState<string[]>();

  useEffect(
    () => setMissingFields(props.formRef?.current?.values?.missing_fields),
    [props.formRef?.current?.values?.missing_fields]
  );

  return (
    <ProfileForm {...props}>
      <FormTextField
        placeholder={t('template:YOUR_PROP', {
          context: 'feminine',
          val: '$t(employee:STREET_NAME_AND_NUMBER)'
        })}
        label={t('employee:STREET_NAME_AND_NUMBER')}
        name="street_name_and_number"
        missing={missingFields?.includes('street_name_and_number')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        xs={12}
      />
      <FormTextField
        placeholder={t('template:YOUR_PROP', {
          val: '$t(company:ADDITION_TO_ADDRESS)'
        })}
        label={t('company:ADDITION_TO_ADDRESS')}
        name="address_supplement"
        xs={12}
      />
      <FormTextField
        placeholder={t('template:YOUR_PROP', {
          context: 'feminine',
          val: '$t(company:ZIP_CODE)'
        })}
        label={t('company:ZIP_CODE')}
        name="zip_code"
        missing={missingFields?.includes('zip_code')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        xs={6}
      />
      <FormTextField
        placeholder={t('template:YOUR_PROP', { context: 'feminine', val: '$t(company:CITY)' })}
        label={t('company:CITY')}
        name="city"
        missing={missingFields?.includes('city')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        xs={6}
      />
      <FormSearchableSelect
        label={t('company:PROVINCE')}
        placeholder={t('template:YOUR_PROP', { val: '$t(company:PROVINCE)' })}
        name="province"
        options={provinceNames}
        xs={6}
      />
      <FormSearchableSelect
        label={t('company:COUNTRY')}
        placeholder={t('template:YOUR_PROP', { val: '$t(company:COUNTRY)' })}
        name="country"
        options={countryNames}
        missing={missingFields?.includes('country')}
        missingMessage={t('employee:MISSING_FOR_COMPLETENESS')}
        additionalOnChange={(e) => {
          setCountry(e as CountrySlug);
        }}
        xs={6}
      />
    </ProfileForm>
  );
};
